import { logInfo, logError } from '@/lib/logger'
import { NotificationService } from './notification-service'
import { GlobalNotificationService } from './global-notification-service'
import { authService } from './auth-service'
import { clearLocalStorage as clearWindowState } from './window-state-service'

/**
 * Login-/Logout-Verarbeitung (Azure AD/MSAL): Notifications beim Login,
 * lokale Aufräumarbeiten beim Logout.
 */
export class AuthSyncService {
  // Nach dem Azure-AD-Redirect rufen sowohl `initialize()` als auch die
  // LoginPage `onUserLogin()` auf — ohne Merker lief alles doppelt.
  private static loggedInUserId: string | null = null

  /**
   * Wird beim Login aufgerufen. Lädt die Notifications.
   *
   * Settings werden hier bewusst NICHT geladen: das macht der
   * `SettingsProvider`, der erst hinter `ProtectedRoute` montiert wird (#908).
   */
  static async onUserLogin(): Promise<void> {
    const userId = authService.getCachedUser()?.id ?? null
    if (userId && userId === this.loggedInUserId) return
    this.loggedInUserId = userId

    try {
      const [notifications, unreadCount] = await Promise.all([
        NotificationService.getNotifications(),
        NotificationService.getUnreadCount()
      ])

      GlobalNotificationService.setNotifications(notifications, unreadCount)
      window.dispatchEvent(new CustomEvent('notifications-loaded', {
        detail: {
          notifications,
          unreadCount,
          timestamp: new Date().toISOString()
        }
      }))

      logInfo(`Notifications geladen: ${notifications.length} total, ${unreadCount} ungelesen`)
    } catch (error) {
      logError(error as Error, { context: 'Notifications beim Login laden' })
    }
  }

  /**
   * Wird beim Logout aufgerufen
   */
  static async onUserLogout(): Promise<void> {
    this.loggedInUserId = null
    try {
      logInfo('Benutzer hat sich abgemeldet, bereinige lokale Einstellungen...')

      // Alle benutzerspezifischen Settings und Fenster-State aus localStorage löschen
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('cdb-webos-settings-') || key.startsWith('cdb-window-state-'))) {
          keysToRemove.push(key)
        }
      }

      // Auch die allgemeinen Settings/Window-State-Einträge löschen (falls vorhanden)
      if (localStorage.getItem('cdb-webos-settings')) {
        keysToRemove.push('cdb-webos-settings')
      }
      if (localStorage.getItem('cdb-window-state')) {
        keysToRemove.push('cdb-window-state')
      }

      // MSAL User Cache löschen (cdb_current_user ist der live MSAL-User-Cache)
      localStorage.removeItem('cdb_current_user')

      // Skip-Restore-Flag setzen, damit Fenster nach Re-Login nicht wiederhergestellt werden
      clearWindowState()

      // Alle gefundenen Settings-Keys löschen
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
        logInfo(`Lokale Settings gelöscht: ${key}`)
      })

      // Globalen Notification Service zurücksetzen
      GlobalNotificationService.reset()

      // Event auslösen, um andere Komponenten zu benachrichtigen
      window.dispatchEvent(new CustomEvent('user-logged-out'))

      logInfo('Logout-Verarbeitung abgeschlossen')
    } catch (error) {
      logError(error as Error, { context: 'Logout-Verarbeitung' })
    }
  }

  /**
   * Initialisiert die Auth-Synchronisation (Azure AD/MSAL)
   * Sollte beim App-Start aufgerufen werden
   */
  static async initialize(): Promise<void> {
    // Prüfe, ob bereits ein User im Cache existiert UND die Session wiederhergestellt werden kann.
    // getCachedUser() allein reicht nicht — das MSAL-Token muss erst via tryRestoreAuth()
    // erneuert werden, damit API-Calls in onUserLogin() funktionieren.
    const cachedUser = authService.getCachedUser()
    if (cachedUser) {
      const isAuthenticated = await authService.isAuthenticated()
      if (isAuthenticated) {
        logInfo('Auth-Session wiederhergestellt, synchronisiere Einstellungen...')
        await this.onUserLogin()
      } else {
        logInfo('Cached User gefunden, aber Auth-Session konnte nicht wiederhergestellt werden')
      }
    }

    // Event Listener für Auth-Events
    window.addEventListener('user-login', () => {
      this.onUserLogin()
    })

    window.addEventListener('user-logout', () => {
      this.onUserLogout()
    })

    logInfo('Auth-Synchronisation Service initialisiert (Azure AD/MSAL)')
  }
}
